import Add from '@mui/icons-material/Add';
import Star from '@mui/icons-material/Star';
import StarBorder from '@mui/icons-material/StarBorder';
import { Box, Button, Card, CardContent, Grid, Skeleton, Stack, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AISolutionId,
  EnterpriseApplyPayload,
  IAISolution,
  // IAISolutionResponse,
  IAllAgents,
  IEnhancedAISolution,
  IMutationAISolution,
} from '../../common/types/common.ts';
import Lists from './List.tsx';
import SectionHeader from './SectionHeader.tsx';
import TopBenefits from './TopBenefits.tsx';
// import { GET_AI_SOLUTION_BY_ID } from '../../graphql/queries/commonQueries';
import { useGraphQLMutation } from '../../customHooks/useGraphQL.ts';
// import { generateDataSource } from '../../services/commonService';
import { Alert, Snackbar } from '@mui/material';
import Loader from '../../common/components/Loader.tsx';
import Modal from '../../common/components/Modal/Modal.tsx';
import { getBenefitLabelNew, getBenefitTitle } from '../../common/utils.tsx';
import { API_BASE, API_LLM_BASE, ENVIRONMENT_TYPE } from '../../config/env.ts';
import { httpConstants } from '../../constants/index.ts';
import { UPDATE_FAVORITE_AI_SOLUTIONS } from '../../graphql/mutations/common.ts';
import { getEnterpriseTechnology } from '../../services/commonService.tsx';
import { httpService } from '../../utility/httpService.ts';
// import { set } from 'react-hook-form';
// import CustomTooltip from '../../common/components/CustomToolTip/CustomToolTip.tsx';

// interface Agent {
//   agent_name: string;
//   agent_description: string;
//   agent_enabler: string[];
// }

// interface AgentResponse {
//   llm_agent: Agent[];
//   solution_enabler: string[];
// }
interface ExtraAgentData {
  agent_name: string;
  implementation_type: string;
  justification: string;
}

// Helper function to check if sync is needed based on timestamp comparison
const checkIfSyncNeeded = async (enterpriseTechResponse: any, agent: any): Promise<boolean> => {
  try {
    // Check if both timestamps exist
    if (!enterpriseTechResponse?.updated_at || !agent?.updated_at) {
      return false;
    }

    // Parse timestamps - both come without timezone info, treat as UTC
    let techDate, agentDate;

    // Both timestamps have no timezone info, treat as UTC
    // Add 'Z' suffix to force UTC interpretation
    const techTimestamp = enterpriseTechResponse.updated_at.endsWith('Z')
      ? enterpriseTechResponse.updated_at
      : enterpriseTechResponse.updated_at + 'Z';
    const agentTimestamp = agent.updated_at.endsWith('Z')
      ? agent.updated_at
      : agent.updated_at + 'Z';

    techDate = new Date(techTimestamp);
    agentDate = new Date(agentTimestamp);

    const techUpdatedAt = techDate.getTime();
    const agentUpdatedAt = agentDate.getTime();

    // Compute difference and require a minimum gap to avoid noisy re-syncs
    const timeDiff = techUpdatedAt - agentUpdatedAt; // tech newer -> positive

    // If tech is not newer, no sync
    if (timeDiff <= 0) return false;

    // Require that tech is sufficiently newer to justify a sync (ignore small diffs)
    const MIN_DIFF_MS = 5 * 60 * 1000; // 5 minutes
    if (timeDiff <= MIN_DIFF_MS) {
      // Small time differences can occur due to minor clock skew; skip sync
      return false;
    }

    // Additional validation: warn if the difference is very large
    const absDiff = Math.abs(timeDiff);
    const MAX_DIFF_MS = 24 * 60 * 60 * 1000; // 24 hours
    if (absDiff > MAX_DIFF_MS) {
      console.warn(
        'Timestamp difference is suspiciously large:',
        absDiff / (60 * 60 * 1000),
        'hours'
      );
    }

    return true;
  } catch (error) {
    console.error('Error comparing timestamps:', error);
    return false;
  }
};

// Helper function to make agent sync API call
const makeAgentSyncAPICall = async (
  aiSolutionData: any,
  enterpriseTechResponse: any,
  additionalPayload?: any
) => {
  try {
    const { company_name, description, industry_data } = enterpriseTechResponse;
    const {
      id,
      roi,
      impact,
      benefits,
      complexity,
      data_sources,
      key_requirements,
      opportunity_name,
    } = aiSolutionData || {};

    const basePayload = {
      company_info: {
        company_name,
        description,
      },
      technology: industry_data,
      aiSolution: {
        solution_id: id,
        roi,
        impact,
        benefits,
        complexity,
        data_sources,
        key_requirements,
        opportunity_name,
        description: aiSolutionData?.description || '',
      },
    };

    // Merge with additional payload if provided (for main flow)
    const payload = additionalPayload
      ? { ...basePayload, ...additionalPayload }
      : {
        ...basePayload,
        llm_agent: aiSolutionData?.llm_agent,
        solution_enabler: aiSolutionData?.solution_enabler,
        diagram_id: aiSolutionData?.diagram_id,
        organization_id: localStorage.getItem('current_organization') || '',
        type: ENVIRONMENT_TYPE,
        label: aiSolutionData?.label,
      };

    const agentUrl = `${API_BASE}/api/v1/simulation/orchestrator/solution-details/`;

    const response = await httpService<any>(httpConstants.METHOD_TYPE.POST, agentUrl, payload);

    return response;
  } catch (error) {
    console.error('Error making agent sync API call:', error);
    throw error;
  }
};

type ViewModalProps = {
  selectedSolutionId: AISolutionId;
  // onAddToSolutionXPLR?: () => void; // <-- REMOVE if not used
  enterpriseContextApplied?: boolean;
  setEnterpriseContextApplied?: (value: boolean) => void;
  aiSolutionData?: any;
  setAiSolutionData?: any;
  diagramId?: string;
};

const ViewModal: React.FC<ViewModalProps> = ({
  selectedSolutionId,
  // onAddToSolutionXPLR, // <-- REMOVE if not used
  enterpriseContextApplied = false,
  setEnterpriseContextApplied = () => { },
  aiSolutionData,
  setAiSolutionData,
  diagramId,
}) => {
  const [solution, setSolution] = useState<IEnhancedAISolution | null>(null);
  const [, setSource] = useState<string[]>([]);
  const [challenge, setChallenge] = useState<string[]>([]);
  // const [agentList, setAgentList] = useState<AgentCardProps[]>([]); // <-- REMOVE if not used
  const [isFavorite, setIsFavorite] = useState<boolean>(false);
  const [isApplyingContext, setIsApplyingContext] = useState<boolean>(false);
  const [isLoadingImplementation, setIsLoadingImplementation] = useState<boolean>(false);
  const [, setDataSources] = useState<string>('');
  const [, setImplementationConsiderations] = useState<string>('');
  const [showApplySuccessSnackbar, setShowApplySuccessSnackbar] = useState(false);
  const [activePiece, setActivePiece] = useState<string>('information');

  const [agentLlmResponse, setAgentLlmResponse] = useState<ExtraAgentData[]>([]);
  const [isLoadingAgentLlmResponse, setIsLoadingAgentLlmResponse] = useState<boolean>(false);

  // --- ADDED for video fix
  // const [videoUrl, setVideoUrl] = useState<string>('');
  // const [videoPoster, setVideoPoster] = useState<string>('');

  // Use refs to track API calls and prevent multiple calls
  const isApplyingContextRef = useRef<boolean>(false);
  const hasAppliedContextRef = useRef<boolean>(false);
  const currentSolutionIdRef = useRef<AISolutionId | null>(null);
  const hasLoadedImplementationRef = useRef<boolean>(false);
  // Add ref to prevent duplicate enterprise technology API calls
  const hasFetchedEnterpriseTechRef = useRef<boolean>(false);

  const currentOrg = useMemo(() => localStorage.getItem('current_organization') || 'Empty', []);
  const [showFavoriteModal, setShowFavoriteModal] = useState(false);

  // Get diagram ID from props or URL parameters
  const getDiagramId = useMemo(() => {
    if (diagramId) return diagramId;

    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('diagram_id') || '';
  }, [diagramId]);

  // Add state for LLM response
  const [llmResponse, setLlmResponse] = useState<string[]>([]);
  // Add state to track enterprise technology loading
  const [isLoadingEnterpriseTech, setIsLoadingEnterpriseTech] = useState<boolean>(false);

  // Admin sync logic - track clicks for manual sync trigger
  const [agentClickCount, setAgentClickCount] = useState<number>(0);
  const [lastClickTime, setLastClickTime] = useState<number>(0);
  const [isManualSyncLoading, setIsManualSyncLoading] = useState<boolean>(false);

  // Manual sync function for admin triple-click
  const triggerManualSync = useCallback(async () => {
    if (!aiSolutionData || isManualSyncLoading) return;

    setIsManualSyncLoading(true);
    console.log('🔄 Admin manual sync triggered');

    try {
      const enterpriseTechResponse = await getEnterpriseTechnology(currentOrg);
      const additionalPayload = {
        llm_agent: aiSolutionData?.llm_agent,
        solution_enabler: aiSolutionData?.solution_enabler,
        diagram_id: getDiagramId,
        organization_id: currentOrg,
        type: ENVIRONMENT_TYPE,
      };

      const syncResponse = await makeAgentSyncAPICall(
        aiSolutionData,
        enterpriseTechResponse,
        additionalPayload
      );

      // Update data with sync response
      if (syncResponse?.agent_classification) {
        setAgentLlmResponse(syncResponse.agent_classification);
      }
      if (syncResponse?.application_in_use) {
        setLlmResponseData(syncResponse.application_in_use);
      }

      console.log('✅ Admin manual sync completed successfully');
    } catch (error) {
      console.error('❌ Admin manual sync failed:', error);
    } finally {
      setIsManualSyncLoading(false);
    }
  }, [aiSolutionData, currentOrg, getDiagramId, isManualSyncLoading]);

  // Handle triple-click on Agents tab for admin sync
  const handleAgentTabClick = useCallback(
    (tabKey: string) => {
      const currentTime = Date.now();

      if (tabKey === 'agents') {
        // Reset count if more than 2 seconds have passed
        if (currentTime - lastClickTime > 2000) {
          setAgentClickCount(1);
          console.log('🔄 Admin sync: First click on Agents tab');
        } else {
          const newCount = agentClickCount + 1;
          setAgentClickCount(newCount);
          console.log(`🔄 Admin sync: Click ${newCount}/3 on Agents tab`);
        }

        setLastClickTime(currentTime);

        // Trigger manual sync on 3rd click
        if (agentClickCount + 1 === 3) {
          console.log('🚀 Admin sync: Triple-click detected! Triggering manual sync...');
          triggerManualSync();
          setAgentClickCount(0); // Reset count
        }
      } else {
        // Reset count if clicking on other tabs
        if (agentClickCount > 0) {
          console.log('🔄 Admin sync: Switched tabs, resetting click count');
          setAgentClickCount(0);
        }
      }

      // Always update the active piece
      setActivePiece(tabKey);
    },
    [agentClickCount, lastClickTime, triggerManualSync]
  );

  // const navigate = useNavigate(); // <-- REMOVE if not used

  // const {
  //   loading: queryLoading,
  //   error: queryError,
  //   data: aiSolutionData,
  // } = useGraphQLQuery<IAISolutionResponse>(
  //   GET_AI_SOLUTION_BY_ID,
  //   {
  //     v1: {
  //       id: selectedSolutionId,
  //     },
  //   },
  //   {
  //     fetchPolicy: 'network-only',
  //   }
  // );

  const [queryLoading, setQueryLoading] = useState(false);
  const [queryError, setQueryError] = useState<any>(null);
  const [llmResponseLoading, setLlmResponseLoading] = useState<boolean>(false);
  const [llmResponseData, setLlmResponseData] = useState<any>([]);

  const fetchAISolutionByID = useCallback(async (): Promise<any> => {
    setQueryLoading(true);
    setQueryError(null);

    // Reset enterprise technology fetch flag for new solution
    hasFetchedEnterpriseTechRef.current = false;
    setIsLoadingEnterpriseTech(false);
    setLlmResponse([]); // Clear previous LLM response

    try {
      // Use the new endpoint with diagram_id and organization_id parameters
      const baseUrl = `${API_BASE}/api/v1/e2e_taxonomy/opportunities/with-agent`;
      const url = `${baseUrl}/${selectedSolutionId}/?diagram_id=${getDiagramId}&organization_id=${currentOrg}`;

      const response = await httpService<any>(httpConstants.METHOD_TYPE.GET, url, undefined);

      if (!response) {
        throw new Error('Invalid response structure');
      }

      setAiSolutionData(response);

      return response;
    } catch (err: any) {
      console.error('Error fetching solution by ID:', err);
      setQueryError(err);
      throw err;
    } finally {
      setQueryLoading(false);
    }
  }, [selectedSolutionId, getDiagramId, currentOrg]);

  // 👇 auto-fetch when selectedSolutionId changes
  useEffect(() => {
    if (selectedSolutionId) {
      fetchAISolutionByID();
    }
  }, [selectedSolutionId, fetchAISolutionByID]);

  useEffect(() => {
    const fetchEnterpriseTechnology = async () => {
      // Prevent duplicate API calls with more robust checking
      if (hasFetchedEnterpriseTechRef.current || !aiSolutionData || !aiSolutionData.id) {
        return;
      }

      try {
        // Mark as fetched to prevent duplicate calls
        hasFetchedEnterpriseTechRef.current = true;
        setIsLoadingEnterpriseTech(true);

        // Check if agent information is already available in the response
        if (aiSolutionData.agent_available === true && aiSolutionData.agent?.embedded) {
          // Only check for sync if agent is not null and has updated_at
          if (aiSolutionData.agent && aiSolutionData.agent.updated_at) {
            const enterpriseTechResponse = await getEnterpriseTechnology(currentOrg);
            const shouldSync = await checkIfSyncNeeded(
              enterpriseTechResponse,
              aiSolutionData.agent
            );

            if (shouldSync) {
              setIsLoadingAgentLlmResponse(true);
              setLlmResponseLoading(true);

              try {
                const syncResponse = await makeAgentSyncAPICall(
                  aiSolutionData,
                  enterpriseTechResponse
                );

                // Update data with sync response
                if (syncResponse?.agent_classification) {
                  setAgentLlmResponse(syncResponse.agent_classification);
                }
                if (syncResponse?.application_in_use) {
                  setLlmResponseData(syncResponse.application_in_use);
                }
              } catch (error) {
                console.error('Sync failed:', error);
                // Fall back to existing data
                setAgentLlmResponse(aiSolutionData.agent.embedded);
                if (aiSolutionData.agent?.ai_solution) {
                  setLlmResponseData(aiSolutionData.agent.ai_solution);
                }
              } finally {
                setIsLoadingAgentLlmResponse(false);
                setLlmResponseLoading(false);
              }
            } else {
              console.log('Agent is up to date, no sync needed');
              // Set data from existing agent data
              setAgentLlmResponse(aiSolutionData.agent.embedded);
              if (aiSolutionData.agent?.ai_solution) {
                setLlmResponseData(aiSolutionData.agent.ai_solution);
              }
              setIsLoadingAgentLlmResponse(false);
            }
          } else {
            console.log('Agent has no timestamp, skipping sync check');
            // Set data from existing agent data when no timestamp
            setAgentLlmResponse(aiSolutionData.agent.embedded);
            if (aiSolutionData.agent?.ai_solution) {
              setLlmResponseData(aiSolutionData.agent.ai_solution);
            }
            setIsLoadingAgentLlmResponse(false);
          }

          return;
        }

        // Check if organization has subscribed to enterprise technology
        const enterpriseTechResponse = await getEnterpriseTechnology(currentOrg);

        if (
          !enterpriseTechResponse ||
          !enterpriseTechResponse.industry_data ||
          Object.keys(enterpriseTechResponse.industry_data).length === 0 ||
          !enterpriseTechResponse.industry_data.functions ||
          enterpriseTechResponse.industry_data.functions.every(
            (fn: { technologies: any[] }) => !fn.technologies || fn.technologies.length === 0
          )
        ) {
          return;
        }
        setLlmResponseLoading(true);
        setIsLoadingAgentLlmResponse(true);

        // Use the consolidated API call function with additional payload for main flow
        const additionalPayload = {
          llm_agent: aiSolutionData?.llm_agent,
          solution_enabler: aiSolutionData?.solution_enabler,
          diagram_id: getDiagramId,
          organization_id: currentOrg,
          type: ENVIRONMENT_TYPE,
        };
        const llmResponseAgentData = await makeAgentSyncAPICall(
          aiSolutionData,
          enterpriseTechResponse,
          additionalPayload
        );

        setLlmResponseData(llmResponseAgentData.application_in_use);
        setAgentLlmResponse(llmResponseAgentData.agent_classification);
        setIsLoadingAgentLlmResponse(false);
        setLlmResponseLoading(false);
      } catch (error) {
        console.error('Failed to fetch enterprise technology:', error);
        // Reset the flag on error so it can be retried
        hasFetchedEnterpriseTechRef.current = false;
      } finally {
        setIsLoadingEnterpriseTech(false);
      }
    };

    fetchEnterpriseTechnology();
  }, [currentOrg, aiSolutionData]);

  const { mutate } = useGraphQLMutation<IMutationAISolution>(UPDATE_FAVORITE_AI_SOLUTIONS, {
    onCompleted: (data) => {
      console.log('Favorite updated successfully:', data);
    },
    onError: (error) => {
      console.error('Error updating favorite:', error);
    },
    refetchQueries: ['IMutationAISolution'],
  });

  const getEnterpriseAppliedSolution = useCallback(
    async (payload: EnterpriseApplyPayload): Promise<{ aiSolution: IAISolution }> => {
      return httpService<{ aiSolution: IAISolution }>(
        httpConstants.METHOD_TYPE.POST,
        `${API_LLM_BASE}/api/ai/simulation/apply-landscape/`,
        payload
      );
    },
    []
  );

  // Load implementation data when dataRequirements is not available
  const loadImplementationData = useCallback(
    async (solutionId: AISolutionId) => {
      console.log('Loading implementation data for solution:', solutionId);

      // FIXED: Check if already loaded for this solution
      if (isLoadingImplementation || hasLoadedImplementationRef.current) {
        console.log('Skipping implementation data load - already loaded or loading');
        return;
      }

      setIsLoadingImplementation(true);
      hasLoadedImplementationRef.current = true; // Mark as loaded immediately

      // try {
      //   // const response = await generateDataSource(solutionId);
      //   if (response) {
      //     setDataSources(response?.data_sources || '');
      //     setImplementationConsiderations(response.implementation_considerations || '');
      //   }
      // } catch (error) {
      //   console.error('Failed to load implementation data:', error);
      //   hasLoadedImplementationRef.current = false; // Reset on error to allow retry
      // } finally {
      //   setIsLoadingImplementation(false);
      // }
    },
    [isLoadingImplementation]
  );

  // Helper function to process solution data - removed from dependencies to prevent recreation
  const processSolutionData = useCallback((rawSolution: IAISolution): IEnhancedAISolution => {
    // Process data requirements
    const allDataRequirements =
      rawSolution.aiOpportunitiesUsed?.flatMap((item) => item.data_requirements || []).join(', ') ||
      '';

    // Process benefits - matching Angular structure
    const allTopBenefits = rawSolution?.benefits
      ? Object.entries(rawSolution.benefits).map(([key, value]) => {
        return {
          title: getBenefitTitle(key),
          label: getBenefitLabelNew(value as string),
        };
      })
      : [];
    // Process data sources - matching Angular structure
    // const fallbackSources = Array.from(
    //   new Set(
    //     rawSolution.aiOpportunitiesUsed
    //       ?.flatMap((item) => item.data_requirements || [])
    //       .map((req) => req?.trim())
    //       .filter((req) => !!req)
    //   )
    // );

    // const dataSources = rawSolution?.dataSources?.length
    //   ? rawSolution.dataSources
    //   : fallbackSources.length > 0
    //     ? fallbackSources
    //     : [];

    //     console.log('Processed data sources:', dataSources);

    // Process key considerations - matching Angular structure
    const keyConsiderations = rawSolution?.keyConsiderations
      ? rawSolution?.keyConsiderations
      : rawSolution.potentialChallenges
        ?.split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0) || [];

    // Process agents
    const allAgents: IAllAgents | undefined = rawSolution.agentCollaboration?.length
      ? {
        description: rawSolution.agentCollaboration.map((c) => c.collaboration).join(' '),
        agents: rawSolution.agentCollaboration
          .flatMap((c) => c.agents)
          .map((agent) => {
            const match = rawSolution.aiOpportunitiesUsed?.find(
              (o) => o.ai_opportunity_id === agent.agent_id
            );
            const isMandatory = match?.must_have === 'MUST HAVE';
            return {
              label: agent.agent_name,
              mustHave: match?.must_have,
              mustHaveLabel: isMandatory ? 'Essential' : 'Optional',
              className: isMandatory ? 'yellow' : 'blue',
            };
          }),
      }
      : undefined;

    // Ensure impactAssessment exists
    const impact = rawSolution.impact;

    return {
      ...rawSolution,
      allDataRequirements,
      allTopBenefits,
      // dataSources,
      keyConsiderations,
      allAgents,
      impact,
    };
  }, []);

  // Handle toggle favorite
  const handleToggleFavorite = useCallback(() => {
    const newFavoriteState = !isFavorite;
    setIsFavorite(newFavoriteState);
    mutate({
      variables: {
        v1: {
          id: selectedSolutionId,
          favorite: newFavoriteState,
        },
      },
    });
    setShowFavoriteModal(false);
  }, [isFavorite, mutate, selectedSolutionId]);

  // Reset context state when solution changes
  useEffect(() => {
    if (currentSolutionIdRef.current !== selectedSolutionId) {
      currentSolutionIdRef.current = selectedSolutionId;
      hasAppliedContextRef.current = false;
      isApplyingContextRef.current = false;
      hasLoadedImplementationRef.current = false; // FIXED: Reset implementation loading state

      // Clear previous solution data
      setDataSources('');
      setImplementationConsiderations('');
      setSource([]);
      setChallenge([]);

      // Reset admin sync state
      setAgentClickCount(0);
      setLastClickTime(0);
      setIsManualSyncLoading(false);
    }
  }, [selectedSolutionId]);

  // Apply enterprise context - simplified and protected from multiple calls
  const applyContext = useCallback(async () => {
    // Only prevent multiple simultaneous calls, not re-application
    if (!solution || isApplyingContextRef.current || isApplyingContext) {
      console.log('Skipping context application:', {
        noSolution: !solution,
        isApplyingRef: isApplyingContextRef.current,
        isApplyingState: isApplyingContext,
      });
      return;
    }

    // Set both ref and state to prevent multiple calls
    isApplyingContextRef.current = true;
    setIsApplyingContext(true);

    console.log('Starting enterprise context application');

    try {
      const payload: EnterpriseApplyPayload = {
        aiSolution: solution,
        organization_id: currentOrg,
      };

      const response = await getEnterpriseAppliedSolution(payload);
      const rawSolution = response.aiSolution;

      // Process the enterprise-applied solution
      const enhancedSolution = processSolutionData(rawSolution);

      // Directly update states
      setSolution(enhancedSolution);
      setIsFavorite(rawSolution.favorite || false);

      // Challenges
      const potentialChallenges =
        rawSolution.potentialChallenges
          ?.split(',')
          .map((item) => item.trim())
          .filter((item) => item.length > 0) || [];
      setChallenge(potentialChallenges);

      // Agents
      // const agents =
      //   rawSolution.aiOpportunitiesUsed?.map((agent: IAIOpportunity) => ({
      //     title: agent.ai_opportunity_name,
      //     type: agent.must_have,
      //   })) || [];

      const uniqueSources = new Set<string>();
      if (enhancedSolution.dataRequirements && enhancedSolution.dataSources) {
        enhancedSolution.dataSources.forEach(() => {
          // uniqueSources.add(data);
        });
      } else if (enhancedSolution.allDataRequirements) {
        enhancedSolution.allDataRequirements.split(',').forEach((data) => {
          uniqueSources.add(data.trim());
        });
      }
      setSource(Array.from(uniqueSources));

      // FIXED: Only load implementation data if not already loaded and if needed
      if (
        !rawSolution.dataRequirements &&
        !rawSolution.implementationConsiderations &&
        !hasLoadedImplementationRef.current
      ) {
        await loadImplementationData(selectedSolutionId);
      }

      hasAppliedContextRef.current = true;
      console.log('Enterprise context applied successfully');
      setShowApplySuccessSnackbar(true);
    } catch (err) {
      console.error('Error applying enterprise context:', err);
      // Don't reset hasAppliedContextRef on error to allow retry
    } finally {
      isApplyingContextRef.current = false;
      setIsApplyingContext(false);
      setEnterpriseContextApplied(false);
    }
  }, [
    solution,
    currentOrg,
    getEnterpriseAppliedSolution,
    processSolutionData,
    loadImplementationData,
    selectedSolutionId,
    setEnterpriseContextApplied,
    isApplyingContext,
  ]);

  // Initial data loading effect
  useEffect(() => {
    if (queryLoading || !aiSolutionData) return;

    if (queryError) {
      console.error('Error fetching AI solution data:', queryError);
      return;
    }

    const rawSolution = aiSolutionData;
    // console.log('Raw solution data:', rawSolution);

    const enhancedSolution = processSolutionData(rawSolution);

    setSolution(enhancedSolution);
    setIsFavorite(rawSolution.favorite || false);

    // Set potential challenges
    // const potentialChallenges =
    //   rawSolution.potentialChallenges
    //     ?.split(',')
    //     .map((item) => item.trim())
    //     .filter((item) => item.length > 0) || [];
    // setChallenge(potentialChallenges);

    // Set agent list for the UI
    // const agents =
    //   rawSolution.aiOpportunitiesUsed?.map((agent: IAIOpportunity) => ({
    //     title: agent.ai_opportunity_name,
    //     type: agent.must_have,
    //   })) || [];

    // FIXED: Only load implementation data if not already loaded and if needed
    if (
      !rawSolution.dataRequirements &&
      !rawSolution.implementationConsiderations &&
      !hasLoadedImplementationRef.current
    ) {
      loadImplementationData(selectedSolutionId);
    }
  }, [
    aiSolutionData,
    queryLoading,
    queryError,
    selectedSolutionId,
    processSolutionData,
    loadImplementationData,
  ]);

  // useEffect(() => {
  //   if (!solution?.id) return;

  //   const loadVideo = async () => {
  //     const id = String(solution.id);

  //     try {
  //       const video = await getSolutionVideoById(id);

  //       if (video.video_url) {
  //         // setVideoUrl(video.video_url);
  //         // setVideoPoster(video.thumbnail_url);
  //       }
  //     } catch (err) {
  //       console.error('❌ Video fetch error:', err);
  //     }
  //   };

  //   loadVideo();
  // }, [solution?.id]);

  // Handle enterprise context application - allow re-application when button is clicked
  useEffect(() => {
    if (
      enterpriseContextApplied &&
      !isApplyingContextRef.current &&
      solution &&
      !isApplyingContext
    ) {
      applyContext();
    }
  }, [enterpriseContextApplied, solution, applyContext, isApplyingContext]);

  // const handleAddToSolutionXPLR = useCallback(() => {
  //   if (onAddToSolutionXPLR) {
  //     onAddToSolutionXPLR();
  //   } else {
  //     navigate(`/solution-xplr?id=${solution?.id}`);
  //   }
  // }, [navigate, solution?.id, onAddToSolutionXPLR]);

  const solutionData = {
    dataSources: {
      internal: [
        { name: 'Customer Profiles', source: 'Salesforce CRM', type: 'Structured' },
        { name: 'Support Tickets', source: 'ServiceNow Case Management', type: 'Unstructured' },
        { name: 'Knowledge Base Articles', source: 'Salesforce KB', type: 'Structured' },
        {
          name: 'Case Resolution Metrics',
          source: 'Internal Reporting Dashboards',
          type: 'Structured',
        },
      ],
      external: [
        // { name: "Chat Transcripts", source: "Oracle Digital Assistant Conversations", type: "Unstructured" },
        // { name: "Email Threads", source: "Outlook / Gmail Integration", type: "Structured" },
        // { name: "Call Recordings", source: "Contact Center Logs", type: "Unstructured" },
        // { name: "Customer Feedback", source: "Free-text Survey Responses", type: "Unstructured" }
      ],
    },

    enterpriseApplications: [
      // "SAP S/4HANA",
      // "Salesforce Sales Cloud"
    ],
  };

  const renderDataRequirementsContent = useMemo(() => {
    type DataSource = { name: string; type: string; technologies?: string[] };

    // Parse description_llm safely
    let descriptionAssets: { data_asset: string; technology: string[] }[] = [];
    try {
      descriptionAssets = llmResponseData ? llmResponseData : [];
    } catch (err) {
      console.error('Invalid description_llm JSON', err);
    }

    // Helper to transform arrays into {name, type} while preserving technologies from description_llm
    const transformSources = (items: string[] | undefined, type: string): DataSource[] =>
      (items || []).map((name) => {
        const matched = descriptionAssets.find(
          (a) => a.data_asset.toLowerCase() === name.toLowerCase()
        );
        return { name, type, technologies: matched?.technology || [] };
      });

    // 🔹 Use data_sources from aiSolutionData response
    const internalSources: DataSource[] = [
      ...transformSources(aiSolutionData?.data_sources?.internal?.structured, 'Structured'),
      ...transformSources(aiSolutionData?.data_sources?.internal?.unstructured, 'Unstructured'),
    ];

    const externalSources: DataSource[] = [
      ...transformSources(aiSolutionData?.data_sources?.external?.structured, 'Structured'),
      ...transformSources(aiSolutionData?.data_sources?.external?.unstructured, 'Unstructured'),
    ];

    const renderChip = (label: string) => (
      <Typography
        key={label}
        variant="caption"
        sx={{
          borderRadius: '12px',
          px: 1.5,
          py: 0.5,
          borderWidth: '0.5px',
          borderStyle: 'solid',
          borderColor: '#DDE1E6',
          fontSize: '12px',
          lineHeight: '14px',
          color: 'text.primary',
          textAlign: 'center',
          ml: 1,
        }}
      >
        {label}
      </Typography>
    );

    const renderSourceItem = (item: DataSource) => {
      return (
        <Box
          key={`${item.name}`}
          sx={{ mb: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'nowrap' }}>
            <img
              src="/images/db.svg"
              alt=""
              style={{ width: 16, height: 16, padding: 4, marginTop: 2 }}
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '16px', mr: 1 }}>
              {item.name}
            </Typography>
          </Box>
          {renderChip(item.type)}

          {llmResponseLoading ? (
            <Typography
              sx={{
                borderRadius: '12px',
                px: 1,
                borderWidth: '0.5px',
                borderStyle: 'solid',
                borderColor: '#DDE1E6',
                fontSize: '12px',
                lineHeight: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                ml: 1,
                height: '22px',
              }}
            >
              <Skeleton variant="rectangular" height={8} width={70} sx={{ borderRadius: '12px' }} />
            </Typography>
          ) : (
            <Typography sx={{ mt: '4px' }}>
              {item.technologies?.map((tech) => renderChip(tech))}
            </Typography>
          )}
        </Box>
      );
    };

    if (internalSources.length || externalSources.length) {
      return (
        <Grid container direction="column">
          {internalSources.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '16px', mb: 2 }}>
                Internal Data Sources
              </Typography>
              {internalSources.map(renderSourceItem)}
            </Box>
          )}

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '16px', mb: 2 }}>
              External Data Sources
            </Typography>
            {externalSources.length > 0 ? (
              externalSources.map(renderSourceItem)
            ) : (
              <Typography variant="body2" sx={{ fontWeight: 400, fontSize: '14px' }}>
                None
              </Typography>
            )}
          </Box>
        </Grid>
      );
    }

    if (isLoadingImplementation) {
      return (
        <Grid container spacing={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[...Array(8)].map((_, index) => (
              <Box key={index} sx={{ height: '1.5em', bgcolor: 'grey.200', borderRadius: 1 }} />
            ))}
          </Box>
        </Grid>
      );
    }

    return (
      <Grid container spacing={4}>
        <Typography>No data requirements available</Typography>
      </Grid>
    );
  }, [solutionData, aiSolutionData, isLoadingImplementation]);

  const renderImplementationConsiderationsContent = useMemo(() => {
    const keyConsiderationsList = aiSolutionData?.key_requirements || [];

    if (keyConsiderationsList.length) {
      return (
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 12 }}>
            <Lists list={keyConsiderationsList}>
              <img src="/images/Info_implement.svg" />
            </Lists>
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 12 }}>
          <Lists list={challenge}>
            <img src="/images/Info_implement.svg" />
          </Lists>
        </Grid>
      </Grid>
    );
  }, [aiSolutionData?.key_requirements, isLoadingImplementation, challenge]);

  const renderEnterpriseApplicationsContent = useMemo(() => {
    // Show loading state when LLM API is being called
    if (isLoadingEnterpriseTech) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            Loading enterprise applications...
          </Typography>
        </Box>
      );
    }

    // Use ai_solution data if available, otherwise fall back to existing data
    let apps: string[] = [];

    if (llmResponseData && llmResponseData.length > 0) {
      // Extract technology from ai_solution objects
      apps = llmResponseData
        .flatMap((item: any) => item.technology || [])
        .filter((tech: string, index: number, arr: string[]) => arr.indexOf(tech) === index);
    } else if (llmResponse.length > 0) {
      // Handle both string arrays and object arrays (fallback)
      if (typeof llmResponse[0] === 'string') {
        apps = llmResponse;
      } else {
        // Extract technology from objects
        apps = llmResponse
          .flatMap((item: any) => item.technology || [])
          .filter((tech: string, index: number, arr: string[]) => arr.indexOf(tech) === index);
      }
    } else {
      apps = solutionData?.enterpriseApplications || [];
    }

    if (!apps.length) {
      return (
        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
          None
        </Typography>
      );
    }

    return apps.map((app, idx) => (
      <Box
        key={idx}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <img
          src="/images/Enterprise.svg"
          alt="Enterprise Application"
          style={{ width: 16, height: 16 }}
        />
        <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500 }}>
          {app}
        </Typography>
      </Box>
    ));
  }, [llmResponseData, llmResponse, solutionData?.enterpriseApplications, isLoadingEnterpriseTech]);

  // Show loading state with consistent modal size
  if (isApplyingContext) {
    return (
      <Box
        sx={{
          position: 'relative',
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 9999,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box> */}
      </Box>
    );
  }

  // Show error state
  if (queryError) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}
      >
        <Typography color="error">Error loading solution data</Typography>
      </Box>
    );
  }

  // Show empty state
  // if (!solution) {
  //   return (
  //     <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
  //       <Typography>No solution data available</Typography>
  //     </Box>
  //   );
  // }

  const getFirstTwoSentences = (text: string): string => {
    if (!text) return '';

    const sentences = text
      .split('.')
      .map((s) => s.trim())
      .filter(Boolean);
    if (sentences.length === 0) return text;
    if (sentences.length === 1) {
      return sentences[0] + '.';
    }
    return sentences.slice(0, 2).join('. ') + '.';
  };

  const IMPLEMENTATION_STYLES: Record<string, { bg: string; color: string; icon: string }> = {
    'Client Specific': {
      bg: '#F1FFEE',
      color: '#27742A',
      icon: '/images/client_specific.svg',
    },
    Embedded: {
      bg: '#FFF7F3',
      color: '#9A5A37',
      icon: '/images/embedded_icon.svg',
    },
    Extended: {
      bg: '#FFF7DF',
      color: '#B18600',
      icon: '/images/extended_icon.svg',
    },
  };

  function renderInfoBox({
    title,
    titleColor,
    icon,
    text,
    bg,
    extraSx = {},
  }: {
    title: string;
    titleColor: string;
    icon: string;
    text: string;
    bg: string;
    extraSx?: object;
  }) {
    return (
      <Box
        sx={{
          background: bg,
          borderRadius: '6px',
          padding: '12px',
          mt: '18px',
          width: { xs: '100%', sm: '50%' },
          minHeight: '126px',
          ...extraSx,
        }}
      >
        <Box sx={{ display: 'flex' }}>
          <img src={icon} alt="icon" />
          <Typography sx={{ fontWeight: 500, fontSize: '15px', color: titleColor, ml: '8px' }}>
            {title}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '15px', fontWeight: 400, mt: '10px' }}>{text}</Typography>
      </Box>
    );
  }

  useEffect(() => {
    return () => {
      setAiSolutionData(null);
    };
  }, []);

  return queryLoading ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '80vh',
        width: '100%',
      }}
    >
      <Loader message="Fetching Solution" />
    </Box>
  ) : (
    <Grid container spacing={2} sx={{ mb: 15 }}>
      <Grid size={{ xs: 12, md: 10 }} spacing={2}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <CardContent
            sx={{
              border: '1px solid #DDE1E6',
              boxShadow: 'none',
              borderRadius: '10px',
              mb: 2,
              p: 0,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {[
                { key: 'information', label: 'Key Information' },
                { key: 'agents', label: 'Agents' },
              ].map((tab, index) => (
                <Box
                  key={tab.key}
                  sx={{
                    flex: 1,
                    height: '64px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: '16px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    borderLeft: index === 1 ? '1px solid #DDE1E6' : 'none',
                  }}
                  onClick={() => handleAgentTabClick(tab.key)}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: activePiece === tab.key ? '#1A55F9' : '#000000',
                      borderBottom: activePiece === tab.key ? '2px solid #1A55F9' : 'none',
                      pb: activePiece !== tab.key ? '2px' : 0,
                      fontSize: '16px',
                    }}
                  >
                    {tab.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
          {activePiece === 'information' ? (
            <Grid>
              <Card sx={{ mb: 3, boxShadow: 'none' }}>
                <CardContent
                  sx={{
                    border: '1px solid #DDE1E6',
                    boxShadow: 'none',
                    borderRadius: '10px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' }, // column on mobile, row on desktop
                      justifyContent: 'space-between',
                      alignItems:
                        aiSolutionData?.roi_description || aiSolutionData?.complexity_description
                          ? 'flex-start'
                          : 'center',
                      gap: { xs: 2, md: 0 },
                    }}
                  >
                    {/* Overview + Description */}
                    <Box sx={{ flex: 1, mr: { md: 3 }, width: { xs: '100%', md: '100%' } }}>
                      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          Overview
                        </Typography>
                        <Box
                          sx={{
                            background: 'linear-gradient(90deg, #1532B8 24.06%, #1B54F8 100%)',
                            borderRadius: '39px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: { xs: '50%', md: '161px' },
                            height: '33px',
                            opacity: 1,
                            boxShadow: 1,
                            mr: { md: 3 },
                            mt: { xs: 2, md: 0 },
                          }}
                        >
                          <Typography
                            sx={{
                              color: 'white',
                              fontFamily: 'Poppins',
                              fontWeight: 500,
                              fontSize: '16px',
                              lineHeight: '120%',
                              letterSpacing: '-1%',
                              textAlign: 'center',
                              textTransform: 'uppercase',
                            }}
                          >
                            {String(aiSolutionData?.impact)}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {getFirstTwoSentences(aiSolutionData?.description || '')}
                      </Typography>
                    </Box>

                    {/* Impact */}
                    <Box sx={{ width: 'fit-content', alignItems: 'flex-start' }}></Box>

                    {/* ROI + Complexity */}
                    {/* Divider */}

                    <Box
                      sx={{
                        display: 'flex',
                        flex: 0.8,
                        flexDirection: 'row', // always row, even on mobile
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        width: { xs: '100%', md: 'auto' }, // take full width on mobile
                        mt: { xs: 2, md: 0 },
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: '2px',
                          backgroundColor: '#DDE1E6',
                          borderRadius: '1px',
                          alignSelf: 'stretch',
                        }}
                      />
                      {/* ROI */}
                      <Box sx={{ flex: 1, gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 400,
                            fontSize: '20px',
                            color: '#4D5358',
                          }}
                        >
                          ROI
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: '18px',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <img
                            src="/images/GreenDot.svg"
                            alt=""
                            style={{ width: 10, height: 10, marginRight: 3 }}
                          />
                          {aiSolutionData?.roi}
                        </Typography>
                        <Typography sx={{ fontSize: '14px', fontWeight: 400 }}>
                          {aiSolutionData?.roi_description}
                        </Typography>
                      </Box>

                      {/* Divider */}
                      <Box
                        sx={{
                          width: '2px',
                          backgroundColor: '#DDE1E6',
                          borderRadius: '1px',
                          alignSelf: 'stretch',
                        }}
                      />

                      {/* Complexity */}
                      <Box sx={{ flex: 1, gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 400,
                            fontSize: '20px',
                            color: '#4D5358',
                          }}
                        >
                          Complexity
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: '18px',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <img
                            src="/images/GreenDot.svg"
                            alt=""
                            style={{ width: 10, height: 10, marginRight: 3 }}
                          />
                          {aiSolutionData?.complexity}
                        </Typography>
                        <Typography sx={{ fontSize: '14px', fontWeight: 400 }}>
                          {aiSolutionData?.complexity_description}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Grid container spacing={2} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
                {/* Data Sources */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <SectionHeader title="Data Sources" img="/images/data_sources.svg" />
                    <Stack
                      spacing={2}
                      alignItems="baseline"
                      justifyContent="flex-start"
                      sx={{ px: 3, py: 3, border: '1px solid #ccc' }}
                    >
                      {renderDataRequirementsContent}
                    </Stack>
                  </Box>
                </Grid>

                {/* Key Requirements */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ height: 'max-content', display: 'flex', flexDirection: 'column' }}>
                    <SectionHeader title="Key Requirements" img="/images/Info_implement_info.svg" />
                    <Stack
                      spacing={2}
                      alignItems="baseline"
                      justifyContent="flex-start"
                      sx={{ flexGrow: 1, px: 3, py: 3, border: '1px solid #ccc' }}
                    >
                      {renderImplementationConsiderationsContent}
                    </Stack>
                  </Box>

                  {/* Enterprise Application (hidden by default) */}
                  <Box
                    sx={{
                      height: 'max-content',
                      display: 'none',
                      flexDirection: 'column',
                      mt: 2,
                    }}
                  >
                    <SectionHeader
                      title="Enterprise Application In Use"
                      img="/images/Enterprise1.svg"
                    />
                    <Stack
                      spacing={2}
                      alignItems="baseline"
                      justifyContent="flex-start"
                      sx={{ flexGrow: 1, px: 3, py: 3, border: '1px solid #ccc' }}
                    >
                      {renderEnterpriseApplicationsContent}
                    </Stack>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ height: '600px', overflow: 'auto' }}>
              {aiSolutionData?.llm_agent?.map((data: any, index: number) => {
                const matched = (agentLlmResponse || [])?.find(
                  (item: any) => item.agent_name === data.agent_name
                );
                console.log(`Agent ${data.agent_name}:`, { data, matched, agentLlmResponse });
                const styleConfig = matched && IMPLEMENTATION_STYLES[matched?.implementation_type];

                return (
                  <Grid key={index}>
                    <Card sx={{ mb: 3, boxShadow: 'none' }}>
                      <CardContent
                        sx={{
                          border: '1px solid #DDE1E6',
                          borderRadius: '10px',
                          boxShadow: 'none',
                        }}
                      >
                        {/* Header */}
                        <Box sx={{ display: 'flex', alignItems: 'start' }}>
                          <Box sx={{ display: 'flex', flex: 1, mr: 3 }}>
                            <img src="/images/agentIcon.svg" alt="agentIcon" />
                            <Box sx={{ ml: '12px' }}>
                              <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, fontSize: '12px', color: '#1B54F8' }}
                              >
                                {data?.agent_enabler?.join(', ')}
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '18px' }}>
                                {data?.agent_name}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' }, // mobile -> column, desktop -> row
                            gap: '15px',
                          }}
                        >
                          {renderInfoBox({
                            title: 'Task',
                            titleColor: '#3286DB',
                            icon: '/images/taskIcon.svg',
                            text: data?.agent_description,
                            bg: '#F7FBFF',
                            extraSx: {
                              width:
                                matched || isLoadingAgentLlmResponse
                                  ? { xs: '100%', sm: '50%' }
                                  : '100%',
                            },
                          })}

                          {isLoadingAgentLlmResponse ? (
                            <Box
                              sx={{
                                borderRadius: '12px',
                                px: 2,
                                py: 2,
                                fontSize: '12px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                background: '#FAFAFA',
                                gap: 1.5,
                                mt: '18px',
                                width: { xs: '100%', sm: '50%' },
                              }}
                            >
                              <Skeleton
                                variant="rounded"
                                width={90}
                                height={10}
                                animation="wave"
                                sx={{ borderRadius: '8px' }}
                              />
                              <Skeleton
                                variant="rectangular"
                                height={10}
                                width="100%"
                                animation="wave"
                                sx={{ borderRadius: '6px', mt: '10px' }}
                              />
                              <Skeleton
                                variant="rectangular"
                                height={10}
                                width="100%"
                                animation="wave"
                                sx={{ borderRadius: '6px' }}
                              />
                              <Skeleton
                                variant="rectangular"
                                height={10}
                                width="100%"
                                animation="wave"
                                sx={{ borderRadius: '6px' }}
                              />
                            </Box>
                          ) : (
                            matched &&
                            styleConfig &&
                            renderInfoBox({
                              title: matched.implementation_type,
                              titleColor: styleConfig.color,
                              icon: styleConfig.icon,
                              text: matched.justification,
                              bg: styleConfig.bg,
                              extraSx: {
                                width: { xs: '100%', sm: '50%' },
                              },
                            })
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Box>
          )}
        </Box>
      </Grid>
      <Grid size={{ xs: 12, md: 2 }}>
        <TopBenefits
          allTopBenefits={
            aiSolutionData?.benefits
              ? Object.entries(aiSolutionData.benefits).map(([key, value]) => ({
                title: getBenefitTitle(key),
                label: getBenefitLabelNew(value as string),
              }))
              : []
          }
        />
      </Grid>

      <Grid size={{ xs: 12, md: 12 }}></Grid>

      {/* Action buttons at the bottom */}
      <Modal
        isOpen={showFavoriteModal}
        onClose={() => setShowFavoriteModal(false)}
        title={isFavorite ? 'Remove AI Solution from Favorites' : 'Add AI Solution to Favorites'}
        maxWidth="sm"
        height="md"
      >
        <Box
          sx={{
            bgcolor: '#F9FBFF',
            borderRadius: '12px',
            p: 3,
          }}
        >
          <Stack spacing={2} alignItems="center">
            {/* Icon + Message */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isFavorite ? (
                <Star sx={{ color: '#2054F4' }} />
              ) : (
                <StarBorder sx={{ color: '#2054F4' }} />
              )}
              <Typography sx={{ fontSize: '16px', color: '#2c2c2c' }}>
                Are you sure you want to{' '}
                {isFavorite ? 'remove this AI Solution from' : 'add this AI Solution to'} your
                favorites?
              </Typography>
            </Box>

            {/* Button */}
            <Button
              variant="contained"
              onClick={handleToggleFavorite}
              sx={{
                textTransform: 'none',
                bgcolor: '#2054F4',
                borderRadius: '6px',
                px: 3,
                py: 1,
                '&:hover': { bgcolor: '#1A45C6' },
                fontWeight: 'bold',
              }}
              startIcon={<Add />}
            >
              {isFavorite ? 'Remove' : 'Add'}
            </Button>
          </Stack>
        </Box>
      </Modal>
      <Snackbar
        open={showApplySuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowApplySuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowApplySuccessSnackbar(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          AI Solution applied successfully
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default ViewModal;
